import React, { useCallback, useMemo, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import {
  downloadTPRMEvidenceApi,
  getTPRMEvidenceApi,
  rejectTPRMEvidenceApi,
  uploadTPRMEvidenceApi,
  verifyTPRMEvidenceApi,
} from "../../../services/axios/AuthApis/TPRMApis";
import TPRMResourceTable, { StatusBadge } from "./TPRMResourceTable";
import {
  displayTPRMValue,
  formatTPRMDate,
  getTPRMErrorMessage,
} from "./tprmPageUtils";
import useTPRMDialog from "./useTPRMDialog";

const FILTERS = [
  {
    id: "vendor_id",
    definition: {
      variant: "text",
      label: "Vendor ID",
      placeholder: "Enter exact vendor ID",
    },
  },
  {
    id: "assessment_id",
    definition: {
      variant: "text",
      label: "Assessment ID",
      placeholder: "Enter exact assessment ID",
    },
  },
  {
    id: "finding_id",
    definition: {
      variant: "text",
      label: "Finding ID",
      placeholder: "Enter exact finding ID",
    },
  },
];

const ReviewDialog = {
  title: "Review evidence",
  fields: [
    {
      name: "comments",
      label: "Comments",
      type: "textarea",
    },
  ],
};

const RejectDialog = {
  title: "Reject evidence",
  description: "A rejection reason is required.",
  fields: [
    {
      name: "comments",
      label: "Rejection reason",
      type: "textarea",
      required: true,
    },
  ],
};

const EvidenceUploadDialog = ({ onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [vendorId, setVendorId] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [assessmentId, setAssessmentId] = useState("");
  const [findingId, setFindingId] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const dialogRef = useTPRMDialog({
    onClose,
    closeOnEscape: !uploading,
  });

  const submitUpload = async (event) => {
    event.preventDefault();
    if (!file || !vendorId.trim() || !documentType.trim()) {
      setError("Select a file and provide a vendor ID and evidence type.");
      return;
    }
    const data = new FormData();
    data.append("file", file);
    data.append("vendor_id", vendorId.trim());
    data.append("document_type", documentType.trim());
    if (assessmentId.trim()) data.append("assessment_id", assessmentId.trim());
    if (findingId.trim()) data.append("finding_id", findingId.trim());

    setUploading(true);
    setError("");
    try {
      await uploadTPRMEvidenceApi(data);
      onUploaded();
    } catch (requestError) {
      setError(getTPRMErrorMessage(requestError, "Evidence upload failed."));
    } finally {
      setUploading(false);
    }
  };

  const inputClass =
    "mt-1 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/60 p-4">
      <section
        aria-labelledby="evidence-upload-title"
        aria-modal="true"
        className="my-auto w-full max-w-xl rounded-xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-slate-900 sm:p-6"
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <h2
          className="text-lg font-semibold text-slate-900 dark:text-white"
          id="evidence-upload-title"
        >
          Upload evidence
        </h2>
        <form className="mt-4 space-y-4" onSubmit={submitUpload}>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            File
            <input
              accept=".pdf,.docx,.xlsx,.csv,.png,.jpg,.jpeg"
              aria-label="Evidence file"
              className={`${inputClass} py-2`}
              onChange={(event) => setFile(event.target.files?.[0] || null)}
              required
              type="file"
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Vendor ID
            <input
              aria-label="Evidence vendor ID"
              className={inputClass}
              onChange={(event) => setVendorId(event.target.value)}
              required
              value={vendorId}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Evidence type
            <input
              aria-label="Evidence type"
              className={inputClass}
              onChange={(event) => setDocumentType(event.target.value)}
              required
              value={documentType}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Assessment ID (optional)
            <input
              aria-label="Evidence assessment ID"
              className={inputClass}
              onChange={(event) => setAssessmentId(event.target.value)}
              value={assessmentId}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Finding ID (optional)
            <input
              aria-label="Evidence finding ID"
              className={inputClass}
              onChange={(event) => setFindingId(event.target.value)}
              value={findingId}
            />
          </label>
          {error && (
            <p className="text-sm text-rose-700 dark:text-rose-300" role="alert">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm dark:border-slate-700 dark:text-slate-200"
              disabled={uploading}
              onClick={onClose}
              type="button"
            >
              Cancel
            </button>
            <button
              className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
              disabled={uploading}
              type="submit"
            >
              {uploading ? "Uploading..." : "Upload evidence"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

const EvidencePage = () => {
  const { isPlatformAdmin, isCustomerAdmin, isSecurityAnalyst } = useAuth();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [uploadFeedback, setUploadFeedback] = useState(null);
  const fetchPage = useCallback((params) => getTPRMEvidenceApi(params), []);

  const columns = useMemo(
    () => [
      {
        id: "name",
        accessorFn: (evidence) =>
          evidence.original_filename ?? evidence.evidence_id,
        header: "Evidence name / ID",
        size: 220,
        cell: ({ row }) => (
          <div>
            <div>{displayTPRMValue(row.original.original_filename)}</div>
            <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {displayTPRMValue(row.original.evidence_id)}
            </div>
          </div>
        ),
      },
      {
        accessorKey: "vendor",
        id: "vendor_id",
        accessorFn: (evidence) => evidence.vendor,
        header: "Vendor reference",
        size: 180,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        accessorKey: "assessment",
        id: "assessment_id",
        accessorFn: (evidence) => evidence.assessment,
        header: "Assessment reference",
        size: 180,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        accessorKey: "finding",
        id: "finding_id",
        accessorFn: (evidence) => evidence.finding,
        header: "Finding reference",
        size: 170,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        accessorKey: "document_type",
        header: "Evidence type",
        size: 155,
        cell: ({ getValue }) => displayTPRMValue(getValue()),
      },
      {
        accessorKey: "uploaded_at",
        header: "Upload date",
        size: 140,
        cell: ({ getValue }) => formatTPRMDate(getValue()),
      },
      {
        accessorKey: "verification_status",
        header: "Verification status",
        size: 175,
        cell: ({ getValue }) => <StatusBadge value={getValue()} />,
      },
    ],
    [],
  );

  const getActions = useCallback((evidence, { canApprove, actorId }) => {
    const actions = [
      { action: "download", label: "Download" },
    ];
    if (
      canApprove &&
      evidence.verification_status === "Pending Review" &&
      String(evidence.uploaded_by || "") !== actorId
    ) {
      actions.push(
        {
          action: "verify",
          label: "Verify",
          dialog: ReviewDialog,
        },
        {
          action: "reject",
          label: "Reject",
          variant: "danger",
          dialog: RejectDialog,
        },
      );
    }
    return actions;
  }, []);

  const performAction = useCallback(async (evidence, item, values) => {
    if (item.action === "download") {
      const response = await downloadTPRMEvidenceApi(evidence.evidence_id);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = evidence.original_filename || evidence.evidence_id;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      return;
    }
    if (item.action === "verify") {
      return verifyTPRMEvidenceApi(evidence.evidence_id, {
        comments: values.comments || "",
      });
    }
    if (item.action === "reject") {
      return rejectTPRMEvidenceApi(evidence.evidence_id, {
        comments: values.comments,
      });
    }
    throw new Error("Unsupported evidence action.");
  }, []);

  const uploadEvidenceButton =
    isPlatformAdmin || isCustomerAdmin || isSecurityAnalyst ? (
    <button
      className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
      onClick={() => {
        setUploadFeedback(null);
        setUploadOpen(true);
      }}
      type="button"
    >
      Upload evidence
    </button>
    ) : null;

  return (
    <div className="space-y-4">
      {uploadFeedback && (
        <p
          className={`rounded-lg border p-3 text-sm ${
            uploadFeedback.type === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200"
              : "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200"
          }`}
          role={uploadFeedback.type === "success" ? "status" : "alert"}
        >
          {uploadFeedback.message}
        </p>
      )}
      <TPRMResourceTable
        columns={columns}
        description="Access private evidence through the authenticated evidence API."
        fetchPage={fetchPage}
        filters={FILTERS}
        getActions={getActions}
        getRowId={(evidence) => evidence.evidence_id}
        performAction={performAction}
        refreshKey={refreshKey}
        title="Evidence"
        toolbarActions={uploadEvidenceButton}
      />
      {uploadOpen && (
        <EvidenceUploadDialog
          onClose={() => setUploadOpen(false)}
          onUploaded={() => {
            setUploadOpen(false);
            setUploadFeedback({
              type: "success",
              message: "Evidence uploaded successfully.",
            });
            setRefreshKey((current) => current + 1);
          }}
        />
      )}
    </div>
  );
};

export default EvidencePage;
